import { useMemo } from 'react';
import type { NavigateFunction } from 'react-router-dom';

const navItems = [
  { label: 'Home', icon: 'ri-home-5-line', activeIcon: 'ri-home-5-fill', path: '/' },
  { label: 'History', icon: 'ri-time-line', activeIcon: 'ri-time-fill', path: '/history' },
  { label: 'Tutor', icon: 'ri-bard-line', activeIcon: 'ri-bard-fill', path: '/ai-assistant' },
  { label: 'Profile', icon: 'ri-user-line', activeIcon: 'ri-user-fill', path: '/profile' },
];

const PILL_WIDTH = 60;
const GAP = 2;
const TAB_WIDTH = PILL_WIDTH + GAP;

const activeBgLight = 'bg-neutral-100/80';
const activeBgDark = 'bg-white/[0.09]';

interface BottomNavProps {
  inFlow?: boolean;
  dark?: boolean;
  isTabRoute: boolean;
  currentPath: string;
  navigate: NavigateFunction;
  /** When true, hide the nav bar (e.g. keyboard is open) */
  hidden?: boolean;
}

export default function BottomNav({ dark = false, isTabRoute, currentPath, navigate, hidden = false }: BottomNavProps) {
  const activeIndex = useMemo(() => {
    if (currentPath.startsWith('/profile/')) return navItems.findIndex((item) => item.path === '/profile');
    const idx = navItems.findIndex((item) => item.path === currentPath);
    return idx >= 0 ? idx : 0;
  }, [currentPath]);

  const handleTap = (index: number, path: string) => {
    if (index === activeIndex && currentPath === path) return;
    if (isTabRoute) {
      navigate(path, { replace: true });
    } else {
      navigate(path);
    }
  };

  return (
    <div
      data-bottom-navigation
      style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom, 0px))' }}
      className={`absolute bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none px-4 ${
        hidden ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
      }`}
      inert={hidden}
      aria-hidden={hidden}
    >
      {!hidden && <span className="sr-only">Study navigation</span>}
      <nav
        className={`pointer-events-auto flex items-center rounded-[24px] backdrop-blur-[24px] px-1.5 py-1.5 gap-[2px] ${
          dark
            ? 'bg-[#1C1D21]/85 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.5),0_4px_12px_-2px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.06)] border border-white/[0.05]'
            : 'bg-white/85 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.15),0_4px_12px_-2px_rgba(0,0,0,0.08),inset_0_1px_0_0_rgba(255,255,255,0.8)] border border-white/40'
        }`}
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
      >
        <div className="relative flex items-center">
          {/* Animated pill */}
          <div
            className={`absolute h-[34px] rounded-[16px] ${dark ? activeBgDark : activeBgLight} transition-transform duration-[200ms] ease-out`}
            style={{
              width: PILL_WIDTH,
              transform: `translateX(${activeIndex * TAB_WIDTH}px)`,
            }}
          />

          {navItems.map((item, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={item.path}
                onClick={() => handleTap(idx, item.path)}
                style={{
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                }}
                className="relative z-10 flex items-center justify-center w-[60px] h-[38px] rounded-[18px] cursor-pointer outline-none select-none"
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <div className="relative z-10 flex items-center overflow-hidden">
                  <i
                    className={`text-[15px] shrink-0 transition-colors duration-150 ${
                      isActive ? item.activeIcon : item.icon
                    } ${
                      isActive
                        ? dark
                          ? 'text-[#F5F5F7]'
                          : 'text-black'
                        : dark
                          ? 'text-[#6E6E73]'
                          : 'text-neutral-400'
                    }`}
                  />
                  <span
                    className={`shrink-0 whitespace-nowrap text-[11px] font-medium leading-none transition-all duration-200 ease-out ${
                      isActive
                        ? 'w-auto opacity-100 ml-1.5'
                        : 'w-0 opacity-0 ml-0'
                    } ${dark ? 'text-[#F5F5F7]' : 'text-black'}`}
                  >
                    {item.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}